import { observer } from "mobx-react-lite"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import type { ComplexTab } from "@/domains/navigation/components/complex-breadcrumb-dropdown"
import type { ComplexBreadcrumbItem } from "@/domains/navigation/types/complex-breadcrumb-item"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { CL_PRODUCT_COPY } from "@/domains/manual-cl-page/cl-product-copy"
import { ComplexBreadcrumbDropdown } from "@/domains/navigation/components/complex-breadcrumb-dropdown"

export const ManualClV2PositionComplexBreadcrumbApplication: ComplexBreadcrumbItem = {
  type: "complex",
  application: "manual-cl-v2-position",
  render: () => <ManualClV2PositionComplexBreadcrumbApplicationComponent />,
}

export const ManualClV2PositionComplexBreadcrumbApplicationComponent = observer(() => {
  const { address, id } = useParams<{ address: string; id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const scaleApp = useScaleApplication()
  const poolAddress = address?.toLowerCase()

  const positions = scaleApp.ManualClV2.Positions.activePositions.filter(
    (position) => position.pool.address.raw.toLowerCase() === poolAddress,
  )

  const tabs: Array<ComplexTab> = positions.map((position) => {
    const tokenId = position.tokenId.toString()
    const displayId = position.nfpmTokenId?.toString() ?? tokenId
    return {
      id: tokenId,
      label: `Position #${displayId}`,
      tabLabel: `#${displayId}`,
      slug: `/liquidity/cl/v2/${position.pool.address.raw}/manage/${tokenId}`,
      description: `${position.pool.displayName} position`,
    }
  })

  const currentTab =
    tabs.find((tab) => tab.id === id) ??
    (id
      ? {
          id,
          label: `Position #${id}`,
          tabLabel: `#${id}`,
          slug: location.pathname,
          description: "Current position",
        }
      : tabs[0])

  const handleTabSelect = (tab: ComplexTab) => {
    const pathSegments = location.pathname.split("/")
    if (pathSegments.length >= 7 && pathSegments[1] === "liquidity") {
      pathSegments[6] = tab.id
      navigate(pathSegments.join("/") + location.search + location.hash)
      return
    }
    navigate(tab.slug)
  }

  return (
    <ComplexBreadcrumbDropdown
      tabs={tabs.length > 0 ? tabs : currentTab ? [currentTab] : []}
      navigationMode="path"
      invalidTabMessage={`Invalid ${CL_PRODUCT_COPY.v2.shortName} position`}
      currentTabOverride={currentTab}
      onTabSelectOverride={handleTabSelect}
    />
  )
})
